import { ReportDocument } from "@/components/report-print/ReportDocument";
import type {
  ReportColumn,
  ReportDocumentModel,
  ReportTableRow,
} from "@/components/report-print/reportTypes";
import type {
  MonthlySupplierSummaryDocument,
  MonthlySupplierSummarySupplier,
} from "@/features/supplier/types";
import { compactVolumeDisplay } from "./volumeDisplay";

interface MonthlySupplierSummaryPrintDocumentProps {
  document: MonthlySupplierSummaryDocument;
  includeEmpty?: boolean;
  printMode?: "source" | "preview";
}

const SUPPLIER_COLUMNS: ReportColumn[] = [
  { key: "supplier", label: "Fornecedor / volumes", width: "42%" },
  {
    key: "orders",
    label: "Pedidos",
    width: "12%",
    align: "right",
    noWrap: true,
  },
  {
    key: "gross",
    label: "Total vendido",
    width: "23%",
    align: "right",
    noWrap: true,
  },
  {
    key: "commission",
    label: "Comissão",
    width: "23%",
    align: "right",
    noWrap: true,
  },
];

export function buildMonthlySupplierSummaryReportModel(
  document: MonthlySupplierSummaryDocument,
  includeEmpty: boolean,
): ReportDocumentModel {
  const suppliers = document.suppliers.filter(
    (supplier) => includeEmpty || supplier.has_movement,
  );

  return {
    header: {
      brand: "EloLink",
      logoSvg: document.logo_svg,
      documentLabel: "Resumo Mensal Geral",
      title: "Fornecedores",
      subtitle: document.month,
      dateLabel: `${document.period_start} a ${document.period_end}`,
      dateSubLabel: `Taxa de comissão: ${document.commission_rate_formatted}%`,
    },
    sections: [
      {
        id: "monthly-supplier-summary",
        title: "Resumo por fornecedor",
        subtitle: "Volumes e valores consolidados do período",
        summary: {
          volume: compactVolumeDisplay(document.total_volume_summary),
          money: document.total_gross_formatted,
        },
        columns: SUPPLIER_COLUMNS,
        rows: [...suppliers.map(buildSupplierRow), buildTotalRow(document)],
      },
    ],
    footer: {
      brand: document.responsible.name || "EloLink",
      generatedLabel: `Gerado em ${document.generated_at}`,
      documentNumber: document.number,
    },
    continuationContext: "Resumo Mensal Geral · Fornecedores",
  };
}

export function MonthlySupplierSummaryPrintDocument({
  document,
  includeEmpty = true,
  printMode = "source",
}: MonthlySupplierSummaryPrintDocumentProps) {
  return (
    <ReportDocument
      document={buildMonthlySupplierSummaryReportModel(document, includeEmpty)}
      printMode={printMode}
    />
  );
}

function buildSupplierRow(
  supplier: MonthlySupplierSummarySupplier,
): ReportTableRow {
  return {
    id: `monthly-supplier-summary-${supplier.id}`,
    density: "wrapped",
    cells: [
      {
        columnKey: "supplier",
        value: supplier.name,
        secondary: compactVolumeDisplay(supplier.volume_summary),
      },
      {
        columnKey: "orders",
        value: String(supplier.total_orders),
        align: "right",
      },
      { columnKey: "gross", value: supplier.gross_formatted, align: "right" },
      {
        columnKey: "commission",
        value: supplier.commission_formatted,
        align: "right",
      },
    ],
  };
}

function buildTotalRow(
  document: MonthlySupplierSummaryDocument,
): ReportTableRow {
  return {
    id: "monthly-supplier-summary-total",
    tone: "highlight",
    cells: [
      { columnKey: "supplier", value: "TOTAL", emphasis: true },
      {
        columnKey: "orders",
        value: String(document.total_orders),
        align: "right",
        emphasis: true,
      },
      {
        columnKey: "gross",
        value: document.total_gross_formatted,
        align: "right",
        emphasis: true,
      },
      {
        columnKey: "commission",
        value: document.commission_formatted,
        align: "right",
        emphasis: true,
      },
    ],
  };
}
